import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { CensusSnapshot } from 'src/shared/models/census-snapshot.model';
import { JobTitle } from 'src/shared/models/job-title.model';
import { PageInterface } from '../../planner/page/page.interface';
import { PatientRatioRecord } from 'src/shared/models/patient-ratio-record.model';
import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { InpatientShiftRecordService } from 'src/shared/services/shift-record-inpatient.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { StaffingPlan } from 'src/shared/models/staffing-plan.model';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { WorkflowPageComponent } from 'src/app/workflow/base-classes/workflow-page/workflow-page.component';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { JobTitleWithTargetDifference } from 'src/shared/models/job-title-with-target-difference.model';
import { WorkflowPageUtil } from '../../base-classes/workflow-page/workflow-page.util';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { WorkflowConstants } from '../../workflow-constants.util';
import { HsgModal } from '@hhsg/hsg-asc';
import { CurrentBlockStaffingGridModalComponent } from '../current-block-staffing-grid-modal/current-block-staffing-grid-modal.component';
import * as moment from 'moment';
import { StaffingGridConstants } from 'src/app/smt/staffing/staffing-grid/utilities/staffing-grid-constants.util';
import { WeekDay } from '@angular/common';
import { ImportTimeAndAttendanceModalComponent } from '../../import-time-and-attendance-modal/import-time-and-attendance-modal.component';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { Constants } from 'src/shared/utilities/constants.util';

@Component({
  selector: 'smt-enter-current',
  templateUrl: './enter-current.component.html',
  styleUrls: ['./enter-current.component.scss']
})
export class InpatientEnterCurrentComponent extends WorkflowPageComponent implements OnInit, PageInterface {
    @Input() page: WorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public loading = true;
    public shiftRecord: ShiftRecord;
    public censusForm: UntypedFormGroup = new UntypedFormGroup({
        admits: new UntypedFormControl('', Validators.required),
        discharges: new UntypedFormControl('', Validators.required)
    });
    public censusFormValues$;

    public ratioForm: UntypedFormGroup = new UntypedFormGroup({});
    public ratioFormValues$;
    public ratioTargets = {};

    public ratioStaffTarget: number = 0;
    public ratioTotal: number = 0;

    public staffForm: UntypedFormGroup = new UntypedFormGroup({});
    public staffFormValues$;

    public formGroups: UntypedFormGroup[];

    public midnightForm: UntypedFormGroup = new UntypedFormGroup({});
    public midnightControl = new UntypedFormControl('', Validators.required);

    public projectedCensus = 0;
    public currentCensus: number;
    public minValue=WorkflowConstants.InpatientMinValue;
    public maxValue=WorkflowConstants.InpatientMaxValue;

    public jobTitlesWithVariance: JobTitleWithTargetDifference[];

    private enableFixedJobEditing = false;
    private enableVariableJobEditing = false;
    public showFixedStaff = false;

    constructor(
        private _workflowUtilityService: WorkflowUtilityService,
        private _inpatientShiftRecordService: InpatientShiftRecordService,
        private _router: Router,
        private modal: HsgModal) {
        super(_router);
    }

    ngOnInit() {
        super.initialize(this.page, () => this.initializeInpatient());
    }

    back() {
        this.previousRequested.emit();
        return false;
    }

    initializeInpatient() {
        this.currentCensus = this.projectedCensus = this.shiftRecord.patientCensusRecord.patients;

        this.setJobTitleControls();

        if (this.shiftRecord.costCenter.inpatientConfiguration.transfers){
            this.censusForm.addControl('transfersIn', new UntypedFormControl('', Validators.required));
            this.censusForm.addControl('transfersOut', new UntypedFormControl('', Validators.required));
        }

        if (this.isRatioBased()) {
            this.shiftRecord.costCenter.inpatientConfiguration.patientRatios.forEach(ratio => {
                this.ratioForm.addControl(this.getRatioControlName(ratio.id), new UntypedFormControl('', Validators.required));
            });
        }

        this.censusFormValues$ = this.censusForm.valueChanges.subscribe(values => {
            this.updateCensusValue(values);
        });

        this.ratioFormValues$ = this.ratioForm.valueChanges.subscribe(values => {
            this.updateTotalRatioValue(values);
        });

        if (this.shiftRecord.patientRatioRecords && this.shiftRecord.patientRatioRecords.length){
            this.shiftRecord.patientRatioRecords.forEach(ratio => {
                this.ratioForm.get(this.getRatioControlName(ratio.patientRatioId)).setValue(ratio.amount);
            });
        }

        if (this.shiftRecord.projectedCensusChurn){
            this.censusForm.patchValue({
                admits: this.shiftRecord.projectedCensusChurn.admits,
                transfersIn: this.shiftRecord.projectedCensusChurn.transfersIn,
                transfersOut: this.shiftRecord.projectedCensusChurn.transfersOut,
                discharges: this.shiftRecord.projectedCensusChurn.discharges
            });
        }

        if (this.shiftRecord.patientCensusRecord.midnight !== null){
            this.midnightControl.setValue(this.shiftRecord.patientCensusRecord.midnight);
        }

        if(this.isMidnightBlock()){
            this.midnightForm.addControl('midnightControl', this.midnightControl);
        }

        this.formGroups = [ this.censusForm, this.ratioForm, this.staffForm, this.midnightForm ];
    }

    getFixedStaffText(jobTitleId: number){
        const count = this.getFixedStaffCount(jobTitleId);

        return (count !== null && count !== undefined) ? count : '*'
    }

    getRecommendedStaffText(jobTitleId: number){
        const count = this.getRecommendedStaffCount(jobTitleId, this.shiftRecord.patientCensusRecord.patients);

        return (count !== null && count !== undefined) ? count : '*';
    }

    getRatioStaffTarget(jobTitleId: number){
        return this.ratioTargets[jobTitleId];
    }

    getRatioStaffTargetText(jobTitleId: number){
        var count = this.getRatioStaffTarget(jobTitleId);

        return (count !== null && count !== undefined) ? count : '*';
    }

    getStaffControlName(jobTitleId: number){
        return this._workflowUtilityService.getStaffControlName(jobTitleId);
    }

    getRatioControlName(jobTitleId: number){
        return this._workflowUtilityService.getRatioControlName(jobTitleId);
    }

    getRecommendedStaffCount(jobTitleId: number, patientCount: number){
        return this._workflowUtilityService.getRecommendedStaffCount(this.shiftRecord, jobTitleId, patientCount);
    }

    getFixedStaffCount(jobTitleId: number){
        return this._workflowUtilityService.getRecommendedStaffCount(this.shiftRecord, jobTitleId, -1);
    }

    updateCensusValue(values){
        for(let i in values){
            if (values[i] && !Number.isInteger(values[i])){
                // we only allow integers as census values
                this.censusForm.get(i).setValue(Math.round(values[i]));
            }
        }

        this.projectedCensus = this._workflowUtilityService.parseCensusValue(this.currentCensus, values);
    }

    updateTotalRatioValue(values){
        // Total patients entered - used for form validation
        var total = 0;
        // Total number of staff recommended based on values entered
        var target = 0;

        this.shiftRecord.costCenter.inpatientConfiguration.patientRatios.forEach(ratio => {
            // formValue represents number of patients
            // multiplier represents number of patients per staff member
            // formValue/multiplier gives you the decimal number of staff needed for that number of patients.

            let formValue = this.ratioForm.value[this.getRatioControlName(ratio.id)];
            let adjValue = formValue * (ratio.staffs /ratio.patients) ;

            if (formValue){
                target += adjValue;
                total += formValue;
            }
        });

        this.ratioStaffTarget = Math.ceil(target);
        this.ratioTotal = total;
    }

    private updateJobTitlesWithVariance(jobTitle: JobTitle) {
        let recommendedStaffText = this.getRecommendedStaffText(jobTitle.id);

        // Make sure we have a valid target before proceeding.
        if (!MiscUtil.isValidNonNegativeNumber(+recommendedStaffText)) {
            return;
        }

        let actualControl =  this.staffForm.get(this.getStaffControlName(jobTitle.id));

        // Make sure we have a valid actual before proceeding.
        if (!actualControl || !MiscUtil.isValidNumber(actualControl.value)) {
            return;
        }

        let amount = +actualControl.value;
        let recommended = +recommendedStaffText;

        // Ensure the array is initialzed to prevent null reference errors.
        if (!MiscUtil.checkArray(this.jobTitlesWithVariance)) {
            this.jobTitlesWithVariance = [];
        }

        // Remove any previously created variance warnings for the shift block and job title.
        this.jobTitlesWithVariance = this.jobTitlesWithVariance.filter(j => j.jobTitle.id !== jobTitle.id);

        if (amount != recommended) {
            let variance = new JobTitleWithTargetDifference();

            variance.jobTitle = jobTitle;
            variance.targetDifference = amount - recommended;

            this.jobTitlesWithVariance.push(variance);
        }

        this.jobTitlesWithVariance = this._workflowUtilityService.sortJobTitlesWithVariance(
                                                                    this.jobTitlesWithVariance,
                                                                    this.shiftRecord.costCenter.fixedJobTitles,
                                                                    this.shiftRecord.costCenter.variableJobTitles);
        this._workflowUtilityService.setJobTitlesWithVariance(this.jobTitlesWithVariance);
    }

    getVarianceWarnings(){
        return this._workflowUtilityService.getVarianceWarnings();
    }

    isMidnightBlock(){
        // Midnight exists at the point where this rolls over, or is zero
        return this.shiftRecord.shiftBlock.startHour === 0
            || this.shiftRecord.shiftBlock.endHour > 0 && this.shiftRecord.shiftBlock.endHour < this.shiftRecord.shiftBlock.startHour;
    }

    isRatioBased() {
        return !this.shiftRecord.costCenter.inpatientConfiguration.totalPatientCensus && this.shiftRecord.costCenter.inpatientConfiguration.patientRatios.length;
    }

    controlValueOutOfRange(staffFormId: number) : boolean {
        let control = this.staffForm.get(this.getStaffControlName(staffFormId));
      return this._workflowUtilityService.valueOutOfRange(control, this.minValue, this.maxValue)
    }

    isFormValid(): boolean {
        return this.ratioForm &&
            this.ratioForm.valid &&
            this.censusForm &&
            this.censusForm.valid &&
            this.staffForm &&
            this.staffForm.valid &&
            this.isValid();
    }

    isValid(): boolean {
        return this.isCensusFormValid()
            && this.isChurnValid()
            && this.isRatioValid()
            && this.isMidnightValid()
            && this.isValidStaffControls();
    }

    isChurnValid() {
        return this.projectedCensus >= this.shiftRecord.costCenter.inpatientConfiguration.minCensus
            && this.projectedCensus <= this.shiftRecord.costCenter.inpatientConfiguration.maxCensus;
    }

    isRatioValid() {
        if (!this.isRatioBased()) return true;

        return this.ratioTotal == this.currentCensus;
    }

    isMidnightValid(){
        if (!this.isMidnightBlock()) return true;

        if (this.isRatioBased())
            return !(this.midnightControl.value === "" || this.midnightControl.value === null);
        else{
            return !(this.midnightControl.value === "" || this.midnightControl.value === null) && this.checkMidnightMaxCensus();
        }
    }

    isCensusFormValid() : boolean {
        const hasTransfers = this.shiftRecord.costCenter.inpatientConfiguration.transfers;
        return WorkflowPageUtil.isValidCensusForm(this.censusForm, hasTransfers);
    }

    private isValidStaffControls(): boolean {
        let invalidFound = false;

        this.shiftRecord.costCenter.jobTitles.forEach(jobTitle => {
            let staffControl = this.staffForm.get(this.getStaffControlName(jobTitle.id));

            if (!staffControl || !MiscUtil.isValidNonNegativeNumber(staffControl.value)) {
                invalidFound = true;
            }
        });

        return !invalidFound;
    }

    checkMidnightMaxCensus()
    {
        return this.midnightControl.value >= this.shiftRecord.costCenter.inpatientConfiguration.minCensus
            && this.midnightControl.value <= this.shiftRecord.costCenter.inpatientConfiguration.maxCensus;
    }

    showEditedName(){
        return !!this.shiftRecord.user;
    }

    private setJobTitleControls() {
        let validators = [ Validators.required, Validators.min(this.minValue), Validators.max(this.maxValue) ];

        // Get all the job title ids that have import values.
        let jobsWithImports = this.variableJobTitleIdsWithImportedValues();

        // The control for the 'Reviewed' toggle that will be added if the staffing plans have imported values.
        let reviewedControl = new UntypedFormControl(false, Validators.requiredTrue);

        // Initialize the controls for job titles.
        this.shiftRecord.costCenter.jobTitles.forEach(jobTitle => {
            let jobTitleControl: UntypedFormControl;

            // Determine if this is a fixed or variable job title.  Variable job titles that have imported amounts will be disabled by default.
            if (this.shiftRecord.costCenter.variableJobTitles.some(j => j.id == jobTitle.id) &&
                jobsWithImports.some(n => n == jobTitle.id)) {
                jobTitleControl = new UntypedFormControl({ value: '', disabled: true }, validators);
            }
            else if (this.shiftRecord.costCenter.fixedJobTitles.some(j => j.id == jobTitle.id)) {
                jobTitleControl = new UntypedFormControl({ value: this.getFixedStaffText(jobTitle.id), disabled: true }, validators);
            }
            else {
                jobTitleControl = new UntypedFormControl('', validators);
            }

            // Add the job title control to the form.
            this.staffForm.addControl(this.getStaffControlName(jobTitle.id), jobTitleControl);

            // Update the jobTitleControl with values from staffing plans.
            if (MiscUtil.checkArray(this.shiftRecord.staffingPlans) && this.shiftRecord.staffingPlans.some(s => s.jobTitleId == jobTitle.id)) {
                let staffingPlan = this.shiftRecord.staffingPlans.find(s => s.jobTitleId == jobTitle.id);

                if (staffingPlan && MiscUtil.isValidNonNegativeNumber(staffingPlan.amount)) {
                    jobTitleControl.setValue(staffingPlan.amount);
                }
            }

            // Make sure variances are captured when the control is initialized.
            this.updateJobTitlesWithVariance(jobTitle);

            // Set a change subscription to the control rather than the form because the form level subscription
            // will ignore disabled controls when evaluating variances.
            jobTitleControl.valueChanges
                           // There is a bug in angular that causes valueChanges subscriptions to fire twice for number inputs.
                           // distinctUntilChanged is an rxjs operator that can be used as a work around until SMT is upgraded
                           // to a version of angular where this bug is corrected.
                           .pipe(distinctUntilChanged())
                           .pipe(debounceTime(Constants.DebounceTime))
                           .subscribe(value => {
                                this.updateJobTitlesWithVariance(jobTitle);

                                // Updates the staffing plan records and returns whether the new value is different from the existing value.
                                let valueChanged = this.updateStaffingPlan(jobTitle.id, value);

                                // If import values have been used and at least one of the job title values have changed then disable the review toggle.
                                if (jobsWithImports.some(n => n == jobTitle.id) && valueChanged && reviewedControl.enabled) {
                                    reviewedControl.setValue(false);
                                    reviewedControl.disable();
                                }
                            });
        });

        // If the import does need to be used then add the reviewed toggle.
        if (this.hasImportedData()) {
            this.staffForm.addControl('reviewed', reviewedControl);
        }
    }

    setStaffPlans(){
        let existingStaffingPlans = this.shiftRecord.staffingPlans || [];
        this.shiftRecord.staffingPlans = [];

        this.shiftRecord.costCenter.jobTitles.forEach(jobTitle => {
            let plan = existingStaffingPlans.find(s => s.jobTitleId == jobTitle.id) || new StaffingPlan();

            plan.workflowTimeFacility = this.shiftRecord.workflowTimeFacility;
            plan.shiftBlockId = this.shiftRecord.shiftBlockId;
            plan.jobTitleId = jobTitle.id;
            
            // Update the recommendations on the staffing plan.
            plan.recommended = +this.getRecommendedStaffCount(jobTitle.id, this.shiftRecord.patientCensusRecord.patients);

            let actualControl = this.staffForm.controls[this.getStaffControlName(jobTitle.id)];

            // If a valid value exists in the actual control then update the staffing plan that will be saved to the database.
            if (actualControl && MiscUtil.isValidNonNegativeNumber(actualControl.value)) {
                plan.amount = +actualControl.value;
            }

            this.shiftRecord.staffingPlans.push(plan);
        });
    }

    setPatientRatioRecords(){
        let existingRatioRecords = this.shiftRecord.patientRatioRecords || [];
        this.shiftRecord.patientRatioRecords = [];

        this.shiftRecord.costCenter.inpatientConfiguration.patientRatios.forEach(ratio => {
            let ratioRecord = existingRatioRecords.find(r => r.patientRatioId == ratio.id) || new PatientRatioRecord();

            ratioRecord.shiftBlockId = this.shiftRecord.shiftBlockId;
            ratioRecord.workflowTimeFacility = this.shiftRecord.workflowTimeFacility;
            ratioRecord.patientRatioId = ratio.id;
            ratioRecord.ratioLabel = ratio.label;
            ratioRecord.patientRatioId = ratio.id;
            ratioRecord.amount = this.ratioForm.value[this.getRatioControlName(ratio.id)];

            this.shiftRecord.patientRatioRecords.push(ratioRecord);
        });
    }

    setProjectedChurn(){
        this.shiftRecord.projectedCensusChurn = new CensusSnapshot();
        this.shiftRecord.projectedCensusChurn.costCenterId = this.shiftRecord.costCenterId;
        this.shiftRecord.projectedCensusChurn.shiftBlockId = this.shiftRecord.shiftBlockId;
        this.shiftRecord.projectedCensusChurn.workflowTimeFacility = this.shiftRecord.workflowTimeFacility;
        this.shiftRecord.projectedCensusChurn.admits = this.censusForm.value['admits'] || 0;
        this.shiftRecord.projectedCensusChurn.transfersIn = this.censusForm.value['transfersIn'] || 0;
        this.shiftRecord.projectedCensusChurn.transfersOut = this.censusForm.value['transfersOut'] || 0;
        this.shiftRecord.projectedCensusChurn.discharges = this.censusForm.value['discharges'] || 0;
        this.shiftRecord.projectedCensusChurn.isProjected = true;
    }

    setPatientCensus(){
        if (this.isMidnightBlock()){
            this.shiftRecord.patientCensusRecord.midnight = this.midnightControl.value;
        }
    }

    setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done) {
            this.shiftRecord.status = ShiftRecordStatus.EnterReasons;
        }
    }

    save() {
        this.setProjectedChurn();
        this.setPatientRatioRecords();
        this.setPatientCensus();
        this.setStaffPlans();
        this.setStatus();
        return this._inpatientShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
    }

    shouldSkipSave(): boolean {
        return false;
    }

    get ratioStaffLabels() {
        return this.shiftRecord.costCenter.includedInRatio.map((jobTitle: JobTitle) => jobTitle.position).join(", ");
    }

    get hasRatioBasedRecommendation() : boolean {
        return this.isRatioBased()  && this.shiftRecord.costCenter.includedInRatio.length>0;
    }

    openCurrentStaffingGrid() {
        this.modal.open(CurrentBlockStaffingGridModalComponent, {
            data: {
                title: this.formatTitle(),
                shiftRecord: this.shiftRecord,
                size: { width: 'auto', height: '500px' },
                acceptText: 'Close'
            }
        });
    }

    private formatTitle(){
        let dayString: string;
        let nextWorkflowTimeFacility = moment(this.shiftRecord.workflowTimeFacility).add(StaffingGridConstants.DefaultShiftBlockLength, 'hours');

        if(this.shiftRecord.shiftBlock.isWeekday){
            dayString = nextWorkflowTimeFacility.weekday() == WeekDay.Saturday ? 'Weekday & Weekend' : 'Weekday';
        }else{
            dayString = nextWorkflowTimeFacility.weekday() == WeekDay.Monday ? 'Weekend & Weekday' : 'Weekend';
        }

        return dayString + ' Variable Staffing Grid';
    }

    public hasImportedData(): boolean {
        if (!this.shiftRecord.staffingPlans) {
            return false;
        }
        return this.shiftRecord.staffingPlans.some(s => s.importedAmount != null);
    }

    public openImportedValuesModal() {
        this.modal.open(
            ImportTimeAndAttendanceModalComponent,
            {
                width: '90%',
                height: '80%',
                data: {
                    title: 'Time & Attendance Imported Values',
                    shiftBlock: this.shiftRecord.shiftBlock,
                    workflowDate: this.shiftRecord.workflowTimeFacility,
                    costCenter: this.shiftRecord.costCenter
                }
            });
    }

    public hasTimeAndAttendanceImports(): boolean {
        return (this.client && this.client.timeAndAttendance) || this.hasImportedData();
    }

    private toggleVariableJobEditing() {
        this.enableVariableJobEditing = !this.enableVariableJobEditing;

        let jobsWithImports = this.variableJobTitleIdsWithImportedValues();

        jobsWithImports.forEach(jobTitleId => {
            let control = this.staffForm.controls[this.getStaffControlName(jobTitleId)];

            if (control) {
                if (this.enableVariableJobEditing) {
                    control.enable();
                }
                else {
                    control.disable();
                }
            }
        });
    }

    private toggleFixedJobEditing() {
        this.enableFixedJobEditing = !this.enableFixedJobEditing;

        // If the fixed staff is not visible then show it.
        if (!this.showFixedStaff) {
            this.showFixedStaff = true;
        }

        this.shiftRecord.costCenter.fixedJobTitles.map(j => j.id).forEach(jobTitleId => {
            let control = this.staffForm.controls[this.getStaffControlName(jobTitleId)];

            if (control) {
                if (this.enableFixedJobEditing) {
                    control.enable();
                }
                else {
                    control.disable();
                }
            }
        });
    }

    private updateStaffingPlan(jobTitleId: number, amount: number): boolean {
        if (this.shiftRecord && MiscUtil.checkArray(this.shiftRecord.staffingPlans)) {
            let staffingPlan = this.shiftRecord.staffingPlans.find(s => s.jobTitleId == jobTitleId);

            if (staffingPlan) {
                let startingAmount = staffingPlan.amount;

                staffingPlan.amount = amount;

                return startingAmount != amount;
            }
        }

        return false;
    }

    private variableJobTitleIdsWithImportedValues(): number[] {
        if (this.shiftRecord &&
            this.shiftRecord.costCenter &&
            MiscUtil.checkArray(this.shiftRecord.staffingPlans) &&
            MiscUtil.checkArray(this.shiftRecord.costCenter.variableJobTitles)) {
            return this.shiftRecord.staffingPlans.filter(s =>
                                                    this.shiftRecord.costCenter.variableJobTitles.map(j => j.id).some(n => n == s.jobTitleId) &&
                                                    MiscUtil.isValidNumber(s.importedAmount)).map(s => s.jobTitleId);
        }

        return [];
    }

    private toggleFixedStaff() {
        this.showFixedStaff = !this.showFixedStaff;
    }
}
